// Section10.jsx — Android App Development Service
import React from "react";
import { useReveal, useParallax } from "../../componets/useScrollFX";
const androidBanner = "../assets/Android App Development Service.webp";
const consumerImg = "../assets/Consumer Mobile Apps Icon.png";
const enterpriseImg = "../assets/Enterprise Applications Icon.png";
const ecommerceImg = "../assets/Ecommerce Solutions Icon.png";
const marketplaceImg = "../assets/Marketplace Apps Icon.png";
const utilityImg = "../assets/Utility Applications Icon.png";
const productivityImg = "../assets/Productivity Tools Icon.png";
const engagementImg = "../assets/Customer Engagement Platforms Icon.png";

const Section10 = () => {
  const androidApps = [
    {
      title: "Consumer mobile apps",
      image: consumerImg,
    },
    {
      title: "Enterprise applications",
      image: enterpriseImg,
    },
    {
      title: "Ecommerce solutions",
      image: ecommerceImg,
    },
    {
      title: "Marketplace apps",
      image: marketplaceImg,
    },
    {
      title: "Utility applications",
      image: utilityImg,
    },
    {
      title: "Productivity tools",
      image: productivityImg,
    },
    {
      title: "Customer engagement platforms",
      image: engagementImg,
    },
  ];

  const capabilities = [
    "High performance",
    "Device compatibility",
    "Security",
    "Scalability",
    "Reliability",
    "Easy maintenance",
  ];

  const benefits = [
    "Reach broad user audiences",
    "Improve customer accessibility",
    "Support digital transformation",
    "Increase engagement",
    "Drive mobile commerce",
  ];

  const [appsRef, appsVisible] = useReveal();
  const [capRef, capVisible] = useReveal();
  const [imgRef, imgOffset] = useParallax(0.06);

  return (
    <section id="android" className="bg-white py-16 overflow-hidden">
      {/* Banner */}
      <div className="relative h-[380px] bg-[#cd2122] overflow-hidden">
        <div
          ref={imgRef}
          className="absolute inset-0"
          style={{ transform: `translateY(${imgOffset}px) scale(1.05)` }}
        >
          {/* <img src={androidBanner} alt="Android app development service" className="w-full h-full object-cover opacity-80" /> */}
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center relative z-10">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 text-[#8bd450] text-xs font-semibold uppercase tracking-widest mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8bd450] animate-pulse" />{" "}
              Android
            </span>
            <h2 className="text-white text-[32px] md:text-[40px] font-extrabold mb-4 leading-tight">
              Android App Development Service
            </h2>
            <p className="text-lg text-white/85">
              Secure, scalable, and feature-rich Android applications that
              connect you with a massive audience.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 md:px-6 relative z-1">
        <div className="bg-white p-3 md:p-14">
          <p className="text-[#535353] text-[16px] leading-relaxed mb-12 mt-6">
            Android is a leading mobile platform. This makes it a vital channel
            for business growth and customer connection. Our Android app
            development service helps organizations create apps that are secure,
            scalable, and full of features.
          </p>

          {/* Android Apps Grid */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-8">
              Comprehensive Android Development
            </h3>
            <p className="text-[#535353] text-[16px] mb-7">As part of our android application development service, we create:</p>
            <div
              ref={appsRef}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {androidApps.map((item, i) => (
                <div
                  key={i}
                  style={{ transitionDelay: `${i * 60}ms` }}
                  className={`group p-7 bg-[#fafafa] hover:bg-white border border-transparent hover:border-[#cd2122] rounded-2xl transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${
                    appsVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-6"
                  }`}
                >
                  <div className="mb-5">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-14 h-14 object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>

                  <p className="text-[#535353] text-[16px] font-medium">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div ref={capRef} className="grid md:grid-cols-2 gap-12">
            {/* Capabilities — terminal / console styled rail */}
            <div
              className={`transition-all duration-700 ${capVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}
            >
              <h3 className="text-2xl font-bold text-[#1a1a1a] mb-5">
                Android Development Capabilities
              </h3>
              <p className="text-[#535353] text-[16px] mb-7">Our team develops Android applications using modern technologies and best practices to ensure:</p>
              <div className="bg-[#111111] rounded-2xl p-6 font-mono">
                <div className="flex gap-1.5 mb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
                </div>
                <div className="space-y-2.5">
                  {capabilities.map((cap, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 text-[14px] text-[#a6e3a1]"
                    >
                      <span className="text-[#8bd450]">✓</span>
                      <span className="text-gray-200">{cap}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Benefits — vertical timeline */}
            <div
              className={`transition-all duration-700 delay-150 ${capVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}
            >
              <h3 className="text-2xl font-bold text-[#1a1a1a] mb-5">
                Benefits of Android Development
              </h3>
              <p className="text-[#535353] text-[16px] mb-7">Businesses choose Android applications to:</p>
              <div className="relative pl-6 border-l-2 border-[#cd2122]/20 space-y-6">
                {benefits.map((benefit, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-[#cd2122]" />
                    <p className="text-[16px] text-[#535353]">{benefit}</p>
                  </div>
                ))}
              </div>
              <p className="text-[#535353] text-[16px] my-5">Our android app development service focuses on delivering applications that provide both technical excellence and business value.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Section10;
